import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';
import { getOrganizationHeaders } from '../orgContext';
import SubmissionReview from './SubmissionReview';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  ConfirmDialog,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconDelete,
  IconEdit,
  IconMetadata,
} from './components/AdminIcons';

interface Assignment {
  id: number;
  organizationId: number;
  courseId?: number | null;
  categoryId?: number | null;
  examId?: number | null;
  examName?: string | null;
  type: string;
  title: string;
  description?: string | null;
  status: string;
  dueAt?: string | null;
  questionCount: number;
  targetCount: number;
  createdByUserId?: number | null;
  createdByUserName?: string | null;
  createdByUserEmail?: string | null;
}

interface CourseLeafOption {
  id: number;
  name: string;
  pathLabel?: string;
}

interface CategoryLeafOption {
  id: number;
  name: string;
  label?: string;
  pathLabel?: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

interface Classroom {
  id: number;
  name: string;
}

interface ClassroomUserOption {
  userId: number;
  displayName: string;
  email?: string | null;
}

interface AssignmentMappedQuestion {
  id: number;
  questionId: number;
  orderIndex?: number | null;
  marks: number;
  isRequired: boolean;
  questionText: string;
}

interface ExamDefinition {
  id: number;
  type: string;
  name: string;
  timeLimitMinutes: number;
  isActive: boolean;
  courses?: { id: number; name: string }[];
}

interface QuestionBankItem {
  id: number;
  categoryId: number;
  questionText: string;
  difficultyLevel: number;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
}

interface AssignmentBuilderQuestionItem {
  id: number;
  examId: number;
  questionId: number;
  questionText: string;
  categoryId: number;
  difficultyLevel: number;
  isEnabled: boolean;
  orderIndex: number | null;
  marks: number;
  negativeMarks: number;
  imageUrl?: string | null;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
  questionLatex?: string | null;
  answerLatex?: string | null;
  graphLatex?: string | null;
  questionForOpenAI?: string | null;
  promptKey?: string | null;
  promptVersion?: string | null;
  promptModel?: string | null;
  promptResponseMode?: string | null;
  answerExplanationJson?: string | null;
}

interface QuestionFormData {
  categoryId: number | null;
  questionText: string;
  questionPrefix: string;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson: string;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  optionE: string;
  correctOption: string;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex: string;
  questionForOpenAI: string;
  promptKey: string;
  promptVersion: string;
  promptModel: string;
  promptResponseMode: string;
}

interface CategoryLookupItem {
  id: number;
  name: string;
  releaseVersion?: string | null;
}

interface CategoryHierarchy {
  id: number;
  name: string;
  subCategories: CategoryHierarchy[];
}

type SortKey = 'title' | 'createdBy' | 'status' | 'questions' | 'targets';

interface AssignmentRow {
  id: number;
  title: string;
  type: string;
  createdBy: string;
  endDate: string;
  status: string;
  questions: number;
  targets: number;
  assignment: Assignment;
}

type EditorMode = 'idle' | 'create' | 'edit';
type DetailsTab = 'details' | 'questions' | 'targets' | 'submissions';

const API_BASE_URL = `${config.apiBaseUrl}/api`;
const DEFAULT_PAGE_SIZE = 10;
const QUESTION_BANK_PAGE_SIZE = 30;

const buildEmptyQuestionForm = (categoryId: number | null = null): QuestionFormData => ({
  categoryId,
  questionText: '',
  questionPrefix: '',
  answerText: '',
  explanation: '',
  answerExplanation: '',
  answerExplanationJson: '',
  hint: '',
  difficultyLevel: 1,
  isActive: true,
  source: '',
  imageUrl: '',
  optionA: '',
  optionB: '',
  optionC: '',
  optionD: '',
  optionE: '',
  correctOption: '',
  isMultipleChoice: true,
  questionLatex: '',
  answerLatex: '',
  graphLatex: '',
  questionForOpenAI: '',
  promptKey: '',
  promptVersion: '',
  promptModel: '',
  promptResponseMode: '',
});

const normalizeQuestionFormText = (value?: string | null): string => (value || '').trim();

const isQuestionFormMultipleChoiceValid = (formData: QuestionFormData): boolean => {
  if (!formData.isMultipleChoice) {
    return true;
  }

  const options = [formData.optionA, formData.optionB, formData.optionC, formData.optionD, formData.optionE]
    .map((option) => option.trim())
    .filter((option) => option.length > 0);

  return options.length >= 2 && ['A', 'B', 'C', 'D', 'E'].includes(formData.correctOption.trim().toUpperCase());
};

const normalizeAssignmentType = (value?: string | null): 'Exam' | 'Homework' => {
  if ((value ?? '').trim().toLowerCase() === 'exam') {
    return 'Exam';
  }

  return 'Homework';
};

const parseReleaseVersion = (releaseVersion?: string | null): number | null => {
  if (!releaseVersion) return null;

  const normalized = releaseVersion.toLowerCase().trim();
  const numericMatch = normalized.match(/\d+/);
  if (!numericMatch) return null;

  const parsed = Number(numericMatch[0]);
  if (parsed >= 1 && parsed <= 4) {
    return parsed;
  }

  return null;
};

const collectCategoryMapFromLookup = (
  categories: CategoryLookupItem[],
  nameMap: Record<number, string>,
  releaseMap: Record<number, number | null>
) => {
  categories.forEach((category) => {
    nameMap[category.id] = category.name;
    releaseMap[category.id] = parseReleaseVersion(category.releaseVersion);
  });
};

const collectCategoryIds = (categories: CategoryHierarchy[]): number[] => {
  const ids: number[] = [];
  const traverse = (nodes: CategoryHierarchy[]) => {
    nodes.forEach((node) => {
      ids.push(node.id);
      if (node.subCategories?.length) {
        traverse(node.subCategories);
      }
    });
  };

  traverse(categories);
  return ids;
};

const hasMathSyntax = (value?: string | null): boolean => {
  const text = (value || '').trim();
  if (!text) return false;

  return /\$|\\\(|\\\[|\\[a-zA-Z]+|\^|_/.test(text);
};

const prepareForMathJax = (value?: string | null): string => {
  const text = (value || '').trim();
  if (!text) return '';

  const hasDelimiters = /\$|\\\(|\\\[/.test(text);
  const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

  if (!hasDelimiters && looksLikeLatex) {
    return `\\(${text}\\)`;
  }

  return text;
};

const toHtmlMarkup = (value?: string | null): string => {
  const prepared = prepareForMathJax(value);
  if (!prepared) return '';

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
  if (hasHtmlTags) {
    return prepared;
  }

  const escaped = prepared
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(/\r?\n/g, '<br/>');
};
const ManageAssignments: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseLeafOption[]>([]);
  const [courseCategoryOptions, setCourseCategoryOptions] = useState<CategoryLeafOption[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [loadingCourseOptions, setLoadingCourseOptions] = useState(false);
  const [loadingCourseCategories, setLoadingCourseCategories] = useState(false);
  const [examDefinitions, setExamDefinitions] = useState<ExamDefinition[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [users, setUsers] = useState<ClassroomUserOption[]>([]);
  const [assignmentQuestionsCache, setAssignmentQuestionsCache] = useState<Record<number, AssignmentMappedQuestion[]>>({});
  const [assignmentQuestionsLoading, setAssignmentQuestionsLoading] = useState(false);
  const [syncingQuestions, setSyncingQuestions] = useState(false);
  const [assignmentTargetsCache, setAssignmentTargetsCache] = useState<Record<number, { classroomIds: number[]; userIds: number[] }>>({});
  const [assignmentTargetsLoading, setAssignmentTargetsLoading] = useState(false);
  const [supportsTargetsRead, setSupportsTargetsRead] = useState(true);
  const [submissionsCache, setSubmissionsCache] = useState<Record<number, any[]>>({});
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [selectedExamDefinitionId, setSelectedExamDefinitionId] = useState<number | null>(null);
  const [assignmentType, setAssignmentType] = useState('Homework');
  const [title, setTitle] = useState('');
  const [examName, setExamName] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<number[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [editorMode, setEditorMode] = useState<EditorMode>('idle');
  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('details');
  const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
  const [referenceDataLoading, setReferenceDataLoading] = useState(false);
  const [questionBank, setQuestionBank] = useState<QuestionBankItem[]>([]);
  const [builderQuestions, setBuilderQuestions] = useState<AssignmentBuilderQuestionItem[]>([]);
  const [categoryNameMap, setCategoryNameMap] = useState<Record<number, string>>({});
  const [categoryReleaseVersionMap, setCategoryReleaseVersionMap] = useState<Record<number, number | null>>({});
  const [courseCategoryIds, setCourseCategoryIds] = useState<Record<number, number[]>>({});
  const [loadingQuestionBank, setLoadingQuestionBank] = useState(false);
  const [loadingQuestionBuilder, setLoadingQuestionBuilder] = useState(false);
  const [savingQuestionBuilder, setSavingQuestionBuilder] = useState(false);
  const [builderQuestionMessage, setBuilderQuestionMessage] = useState<string | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showAdvancedQuestionFields, setShowAdvancedQuestionFields] = useState(false);
  const [editingQuestionFormId, setEditingQuestionFormId] = useState<number | null>(null);
  const [questionFormData, setQuestionFormData] = useState<QuestionFormData>(buildEmptyQuestionForm());
  const [savingQuestionForm, setSavingQuestionForm] = useState(false);
  const [isUploadingQuestionImage, setIsUploadingQuestionImage] = useState(false);
  const [draggingQuestionId, setDraggingQuestionId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [difficultyFilter, setDifficultyFilter] = useState<number | null>(null);
  const [courseFilter, setCourseFilter] = useState<number | null>(null);
  const [releaseVersionFilter, setReleaseVersionFilter] = useState<number | null>(null);
  const [questionBankPage, setQuestionBankPage] = useState(1);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const assignmentLoadRef = useRef(false);
  const referenceDataLoadRef = useRef(false);
  const builderIntentHandledRef = useRef(false);

  const isBuilderPage = location.pathname === '/manage-assignment-builder';
  const builderModeParam = (searchParams.get('mode') || '').toLowerCase();
  const builderAssignmentIdParam = Number(searchParams.get('assignmentId') || '');
  const builderExamIdParam = Number(searchParams.get('examId') || '');
  const builderTabParam = (searchParams.get('tab') || '').toLowerCase();

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const buildAuthHeaders = (token: string) => ({
    Authorization: `Bearer ${token}`,
    ...getOrganizationHeaders(),
  });

  const loadAssignments = useCallback(async () => {
    if (assignmentLoadRef.current) {
      return;
    }

    try {
      assignmentLoadRef.current = true;
      setLoading(true);
      setError(null);
      const token = await getToken();

      const assignmentResponse = await axios.get(`${API_BASE_URL}/assignments/admin`, {
        headers: buildAuthHeaders(token),
      });

      setAssignments(Array.isArray(assignmentResponse.data) ? assignmentResponse.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load assignments data.');
    } finally {
      setLoading(false);
      assignmentLoadRef.current = false;
    }
  }, [getToken]);

  const loadReferenceData = useCallback(async () => {
    if (referenceDataLoaded || referenceDataLoading) {
      return;
    }

    if (referenceDataLoadRef.current) {
      return;
    }

    try {
      referenceDataLoadRef.current = true;
      setReferenceDataLoading(true);
      setError(null);
      const token = await getToken();
      const authHeaders = buildAuthHeaders(token);

      const [classroomResponse, usersResponse, examDefinitionsResponse, homeworkDefinitionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/classrooms`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/classrooms/users`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/exams/admin?type=Exam`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/exams/admin?type=Homework`, { headers: authHeaders }),
      ]);

      const mergedDefinitions = [
        ...(Array.isArray(examDefinitionsResponse.data) ? examDefinitionsResponse.data : []),
        ...(Array.isArray(homeworkDefinitionsResponse.data) ? homeworkDefinitionsResponse.data : []),
      ];

      const dedupedDefinitions = Array.from(
        new Map(
          mergedDefinitions
            .filter((item) => item && typeof item.id === 'number')
            .map((item) => [item.id, { ...item, type: normalizeAssignmentType(item.type) } as ExamDefinition])
        ).values()
      );

      setClassrooms(Array.isArray(classroomResponse.data) ? classroomResponse.data : []);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setExamDefinitions(dedupedDefinitions);
      setReferenceDataLoaded(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load assignment reference data.');
    } finally {
      setReferenceDataLoading(false);
      referenceDataLoadRef.current = false;
    }
  }, [getToken, referenceDataLoaded, referenceDataLoading]);

  const loadCourseOptions = useCallback(async () => {
    try {
      setLoadingCourseOptions(true);
      const token = await getToken();
      const response = await axios.get<CourseLeafOption[]>(`${API_BASE_URL}/courses/leaf-options`, {
        headers: buildAuthHeaders(token),
      });

      const nextOptions = Array.isArray(response.data) ? response.data : [];
      setCourseOptions(nextOptions);
      setSelectedCourseId((current) => {
        if (current != null && nextOptions.some((course) => course.id === current)) {
          return current;
        }

        return nextOptions[0]?.id ?? null;
      });
    } catch (err: any) {
      setCourseOptions([]);
      setCourseCategoryOptions([]);
      setSelectedCourseId(null);
      setSelectedCategoryId(null);
      setError(err?.response?.data?.message || err?.message || 'Failed to load courses.');
    } finally {
      setLoadingCourseOptions(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void loadAssignments();
    void loadCourseOptions();
  }, [isAuthenticated, loadAssignments, loadCourseOptions]);

  useEffect(() => {
    if (!selectedCourseId) {
      setCourseCategoryOptions([]);
      setSelectedCategoryId(null);
      return;
    }

    const loadCourseCategories = async () => {
      try {
        setLoadingCourseCategories(true);
        const token = await getToken();
        const response = await axios.get<CategoryLeafOption[]>(`${API_BASE_URL}/categories/lookup/leaf?courseId=${selectedCourseId}`, {
          headers: buildAuthHeaders(token),
        });

        const nextCategories = Array.isArray(response.data) ? response.data : [];
        setCourseCategoryOptions(nextCategories);
        setSelectedCategoryId((current) => {
          if (current != null && nextCategories.some((category) => category.id === current)) {
            return current;
          }

          return null;
        });
      } catch (err: any) {
        setCourseCategoryOptions([]);
        setSelectedCategoryId(null);
        setError(err?.response?.data?.message || err?.message || 'Failed to load course categories.');
      } finally {
        setLoadingCourseCategories(false);
      }
    };

    void loadCourseCategories();
  }, [getToken, selectedCourseId]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const needsReferenceData = editorMode !== 'idle' || selectedAssignmentId != null;
    if (!needsReferenceData) {
      return;
    }

    void loadReferenceData();
  }, [isAuthenticated, editorMode, selectedAssignmentId, loadReferenceData]);

  const loadAssignmentQuestions = useCallback(async (assignmentId: number) => {
    let authHeaders: ReturnType<typeof buildAuthHeaders> | null = null;
    const linkedExamId = assignments.find((item) => item.id === assignmentId)?.examId;
    try {
      setAssignmentQuestionsLoading(true);
      setError(null);
      const token = await getToken();
      authHeaders = buildAuthHeaders(token);

      if (linkedExamId) {
        const linkedResponse = await axios.get(`${API_BASE_URL}/exams/${linkedExamId}/questions`, {
          headers: authHeaders,
        });

        const linkedMapped: AssignmentMappedQuestion[] = Array.isArray(linkedResponse.data)
          ? linkedResponse.data.map((item: any, index: number) => ({
              id: Number(item.id ?? item.questionId ?? index + 1),
              questionId: Number(item.questionId ?? item.id ?? 0),
              orderIndex: item.orderIndex ?? index + 1,
              marks: Number(item.marks ?? 1),
              isRequired: true,
              questionText: item.questionText ?? '',
            }))
          : [];

        setAssignmentQuestionsCache((current) => ({
          ...current,
          [assignmentId]: linkedMapped,
        }));
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/assignments/${assignmentId}/questions`, {
        headers: authHeaders,
      });

      setAssignmentQuestionsCache((current) => ({
        ...current,
        [assignmentId]: Array.isArray(response.data) ? response.data : [],
      }));
    } catch (err: any) {
      if (err?.response?.status === 405) {
        if (linkedExamId && authHeaders) {
          try {
            const fallbackResponse = await axios.get(`${API_BASE_URL}/exams/${linkedExamId}/questions`, {
              headers: authHeaders,
            });

            const fallbackMapped: AssignmentMappedQuestion[] = Array.isArray(fallbackResponse.data)
              ? fallbackResponse.data.map((item: any, index: number) => ({
                  id: Number(item.id ?? item.questionId ?? index + 1),
                  questionId: Number(item.questionId ?? item.id ?? 0),
                  orderIndex: item.orderIndex ?? index + 1,
                  marks: Number(item.marks ?? 1),
                  isRequired: true,
                  questionText: item.questionText ?? '',
                }))
              : [];

            setAssignmentQuestionsCache((current) => ({
              ...current,
              [assignmentId]: fallbackMapped,
            }));
            return;
          } catch {
            setError('Failed to load mapped questions from assignment and linked definition.');
          }
        } else {
          setError('Mapped question API is unavailable for this assignment. Link a definition to manage/view questions.');
        }
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load mapped questions.');
      }
    } finally {
      setAssignmentQuestionsLoading(false);
    }
  }, [assignments, getToken]);

  const loadAssignmentTargets = useCallback(async (assignmentId: number) => {
    if (!supportsTargetsRead) {
      return;
    }

    try {
      setAssignmentTargetsLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(`${API_BASE_URL}/assignments/${assignmentId}/targets`, {
        headers: buildAuthHeaders(token),
      });

      const classIds = Array.isArray(response.data?.classroomIds)
        ? response.data.classroomIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
        : [];
      const userIds = Array.isArray(response.data?.userIds)
        ? response.data.userIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
        : [];

      setAssignmentTargetsCache((current) => ({
        ...current,
        [assignmentId]: {
          classroomIds: classIds,
          userIds,
        }
      }));
    } catch (err: any) {
      if (err?.response?.status === 405) {
        setSupportsTargetsRead(false);
        setAssignmentTargetsCache((current) => ({
          ...current,
          [assignmentId]: {
            classroomIds: [],
            userIds: [],
          }
        }));
        setMessage('Target prefill is unavailable on current backend; you can still select and save targets.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load assignment targets.');
      }
    } finally {
      setAssignmentTargetsLoading(false);
    }
  }, [getToken, supportsTargetsRead]);

  const loadSubmissions = useCallback(async (assignmentId: number) => {
    try {
      setSubmissionsLoading(true);
      const token = await getToken();
      const response = await axios.get(`${API_BASE_URL}/assignments/${assignmentId}/submissions`, {
        headers: buildAuthHeaders(token),
      });

      const submissions = Array.isArray(response.data) ? response.data : [];
      setSubmissionsCache((current) => ({
        ...current,
        [assignmentId]: submissions,
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load submissions.');
    } finally {
      setSubmissionsLoading(false);
    }
  }, [getToken]);

  const openSubmissionPreview = useCallback((attemptId: number) => {
    setSelectedSubmissionId(attemptId);
  }, []);

  const closeSubmissionPreview = useCallback(() => {
    setSelectedSubmissionId(null);
  }, []);

  const assignmentQuestions = useMemo(() => {
    if (selectedAssignmentId == null) {
      return [];
    }

    return assignmentQuestionsCache[selectedAssignmentId] ?? [];
  }, [assignmentQuestionsCache, selectedAssignmentId]);

  useEffect(() => {
    const isQuestionsContext = detailsTab === 'questions';

    if (!isAuthenticated || !isQuestionsContext || selectedAssignmentId == null) {
      return;
    }

    if (assignmentQuestionsCache[selectedAssignmentId] || assignmentQuestionsLoading) {
      return;
    }

    void loadAssignmentQuestions(selectedAssignmentId);
  }, [isAuthenticated, detailsTab, selectedAssignmentId, assignmentQuestionsCache, assignmentQuestionsLoading, loadAssignmentQuestions]);

  const loadAssignmentQuestionBuilder = useCallback(async (examId: number) => {
    try {
      setLoadingQuestionBuilder(true);
      setBuilderQuestionMessage(null);
      setError(null);
      const token = await getToken();
      const authHeaders = buildAuthHeaders(token);

      const [examQuestionsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/exams/${examId}/questions`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/categories/lookup`, { headers: authHeaders }),
      ]);

      const categoryMap: Record<number, string> = {};
      const releaseMap: Record<number, number | null> = {};
      collectCategoryMapFromLookup(categoriesResponse.data || [], categoryMap, releaseMap);
      setCategoryNameMap(categoryMap);
      setCategoryReleaseVersionMap(releaseMap);

      const mappedQuestions: AssignmentBuilderQuestionItem[] = (examQuestionsResponse.data || []).map((item: any, index: number) => ({
        id: item.id ?? 0,
        examId,
        questionId: item.questionId,
        questionText: item.questionText || '',
        categoryId: item.categoryId,
        difficultyLevel: item.difficultyLevel,
        isEnabled: item.isEnabled ?? true,
        orderIndex: item.orderIndex ?? index,
        marks: item.marks > 0 ? item.marks : 1,
        negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
      }));

      setBuilderQuestions(mappedQuestions);
      setQuestionBank([]);
      setQuestionBankPage(1);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load question builder.');
    } finally {
      setLoadingQuestionBuilder(false);
    }
  }, [getToken]);

  const loadManualAssignmentQuestionBuilder = useCallback(async (assignmentId: number) => {
    try {
      setLoadingQuestionBuilder(true);
      setBuilderQuestionMessage(null);
      setError(null);
      const token = await getToken();

      const [assignmentQuestionsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/assignments/${assignmentId}/questions`, {
          headers: buildAuthHeaders(token),
        }),
        axios.get(`${API_BASE_URL}/categories/lookup`, {
          headers: buildAuthHeaders(token),
        }),
      ]);

      const categoryMap: Record<number, string> = {};
      const releaseMap: Record<number, number | null> = {};
      collectCategoryMapFromLookup(categoriesResponse.data || [], categoryMap, releaseMap);
      setCategoryNameMap(categoryMap);
      setCategoryReleaseVersionMap(releaseMap);

      const mappedQuestions: AssignmentBuilderQuestionItem[] = (assignmentQuestionsResponse.data || []).map((item: any, index: number) => ({
        id: item.id ?? 0,
        examId: 0,
        questionId: item.questionId,
        questionText: item.questionText || '',
        categoryId: item.categoryId ?? 0,
        difficultyLevel: item.difficultyLevel ?? 0,
        isEnabled: true,
        orderIndex: item.orderIndex ?? index,
        marks: item.marks > 0 ? item.marks : 1,
        negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
      }));

      setBuilderQuestions(mappedQuestions);
      setQuestionBank([]);
      setQuestionBankPage(1);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load assignment questions.');
    } finally {
      setLoadingQuestionBuilder(false);
    }
  }, [getToken]);

  const loadQuestionBankForCategory = useCallback(async (categoryId: number) => {
    try {
      setLoadingQuestionBank(true);
      const token = await getToken();
      const response = await axios.get(`${API_BASE_URL}/questions?categoryId=${categoryId}&isActive=true`, {
        headers: buildAuthHeaders(token),
      });
      setQuestionBank(response.data || []);
      setQuestionBankPage(1);
    } catch (err: any) {
      setQuestionBank([]);
      setError(err?.response?.data?.message || err?.message || 'Failed to load questions for selected category.');
    } finally {
      setLoadingQuestionBank(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isBuilderPage || detailsTab !== 'questions' || !selectedAssignmentId) {
      return;
    }

    if (selectedExamDefinitionId) {
      void loadAssignmentQuestionBuilder(selectedExamDefinitionId);
      return;
    }

    void loadManualAssignmentQuestionBuilder(selectedAssignmentId);
  }, [detailsTab, isBuilderPage, loadAssignmentQuestionBuilder, loadManualAssignmentQuestionBuilder, selectedAssignmentId, selectedExamDefinitionId]);

  useEffect(() => {
    if (!isBuilderPage || detailsTab !== 'questions' || !selectedAssignmentId) {
      return;
    }

    if (!categoryFilter) {
      setQuestionBank([]);
      return;
    }

    void loadQuestionBankForCategory(categoryFilter);
  }, [categoryFilter, detailsTab, isBuilderPage, loadQuestionBankForCategory, selectedAssignmentId]);

  useEffect(() => {
    if (!isBuilderPage || detailsTab !== 'questions' || !selectedAssignmentId || !courseFilter) {
      return;
    }

    if (courseCategoryIds[courseFilter]?.length) {
      return;
    }

    const loadCourseCategories = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${API_BASE_URL}/categories/by-course/${courseFilter}`, {
          headers: buildAuthHeaders(token),
        });

        const ids = collectCategoryIds(response.data || []);
        setCourseCategoryIds((prev) => ({ ...prev, [courseFilter]: ids }));
      } catch {
        // Best-effort data for course-based filtering.
      }
    };

    void loadCourseCategories();
  }, [courseCategoryIds, courseFilter, detailsTab, getToken, isBuilderPage, selectedAssignmentId]);

  useEffect(() => {
    if (!isAuthenticated || detailsTab !== 'targets' || selectedAssignmentId == null || !supportsTargetsRead) {
      return;
    }

    if (assignmentTargetsCache[selectedAssignmentId] || assignmentTargetsLoading) {
      return;
    }

    void loadAssignmentTargets(selectedAssignmentId);
  }, [isAuthenticated, detailsTab, selectedAssignmentId, supportsTargetsRead, assignmentTargetsCache, assignmentTargetsLoading, loadAssignmentTargets]);

  useEffect(() => {
    if (detailsTab !== 'targets' || selectedAssignmentId == null) {
      return;
    }

    const cachedTargets = assignmentTargetsCache[selectedAssignmentId];
    if (!cachedTargets) {
      return;
    }

    setSelectedClassroomIds(cachedTargets.classroomIds);
    setSelectedUserIds(cachedTargets.userIds);
  }, [detailsTab, selectedAssignmentId, assignmentTargetsCache]);

  useEffect(() => {
    if (!isAuthenticated || detailsTab !== 'submissions' || selectedAssignmentId == null) {
      return;
    }

    if (submissionsCache[selectedAssignmentId] || submissionsLoading) {
      return;
    }

    void loadSubmissions(selectedAssignmentId);
  }, [isAuthenticated, detailsTab, selectedAssignmentId, submissionsCache, submissionsLoading, loadSubmissions]);

  const rows = useMemo<AssignmentRow[]>(
    () =>
      assignments.map((assignment) => ({
        id: assignment.id,
        title: assignment.title,
        type: assignment.type,
        createdBy: assignment.createdByUserName || assignment.createdByUserEmail || 'Unknown',
        endDate: assignment.dueAt ?? '',
        status: assignment.status,
        questions: assignment.questionCount,
        targets: assignment.targetCount,
        assignment,
      })),
    [assignments]
  );

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return rows.filter((row) => {
      if (search) {
        const haystack = [row.title, row.type, row.createdBy, row.status, row.questions, row.targets].join(' ').toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter !== 'all' && row.status.toLowerCase() !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((left, right) => {
      const leftValue = String(left[sortKey]).toLowerCase();
      const rightValue = String(right[sortKey]).toLowerCase();
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const getSelectedQuestionIds = () => new Set(builderQuestions.map((item) => item.questionId));

  const addQuestionToBuilder = (question: QuestionBankItem) => {
    const alreadySelected = builderQuestions.some((item) => item.questionId === question.id);
    if (alreadySelected || !selectedAssignmentId) {
      return;
    }

    setBuilderQuestions((prev) => [
      ...prev,
      {
        id: 0,
        examId: selectedExamDefinitionId ?? 0,
        questionId: question.id,
        questionText: question.questionText,
        categoryId: question.categoryId,
        difficultyLevel: question.difficultyLevel,
        isEnabled: true,
        orderIndex: prev.length,
        marks: 1,
        negativeMarks: 0,
      },
    ]);
  };

  const removeQuestionFromBuilder = (questionId: number) => {
    setBuilderQuestions((prev) => prev.filter((item) => item.questionId !== questionId));
  };

  const handleEditMappedQuestion = (item: AssignmentBuilderQuestionItem) => {
    const mappedQuestion: QuestionBankItem = {
      id: item.questionId,
      categoryId: item.categoryId,
      questionText: item.questionText || '',
      difficultyLevel: item.difficultyLevel || 1,
      optionA: item.optionA ?? '',
      optionB: item.optionB ?? '',
      optionC: item.optionC ?? '',
      optionD: item.optionD ?? '',
      optionE: item.optionE ?? '',
      correctOption: item.correctOption ?? '',
    };

    openQuestionForm(mappedQuestion);
  };

  const updateBuilderQuestion = (questionId: number, patch: Partial<AssignmentBuilderQuestionItem>) => {
    setBuilderQuestions((prev) =>
      prev.map((item) =>
        item.questionId === questionId
          ? {
              ...item,
              ...patch,
            }
          : item
      )
    );
  };

  const reorderBuilderQuestions = (sourceQuestionId: number, targetQuestionId: number) => {
    if (sourceQuestionId === targetQuestionId) return;

    setBuilderQuestions((prev) => {
      const sorted = [...prev].sort(
        (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
      );

      const sourceIndex = sorted.findIndex((item) => item.questionId === sourceQuestionId);
      const targetIndex = sorted.findIndex((item) => item.questionId === targetQuestionId);

      if (sourceIndex < 0 || targetIndex < 0) {
        return prev;
      }

      const [movedItem] = sorted.splice(sourceIndex, 1);
      sorted.splice(targetIndex, 0, movedItem);

      return sorted.map((item, index) => ({
        ...item,
        orderIndex: index,
      }));
    });
  };

  const saveBuilderQuestions = async () => {
    if (!selectedAssignmentId) {
      setError('Create the assignment draft first.');
      return;
    }

    try {
      setSavingQuestionBuilder(true);
      setBuilderQuestionMessage(null);
      setError(null);
      const token = await getToken();

      const sortedQuestions = [...builderQuestions].sort(
        (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
      );

      const payload = {
        questions: sortedQuestions.map((item, index) => ({
          questionId: item.questionId,
          isEnabled: item.isEnabled,
          orderIndex: item.orderIndex ?? index,
          marks: item.marks > 0 ? item.marks : 1,
          negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
        })),
      };

      if (selectedExamDefinitionId) {
        const response = await axios.put(`${API_BASE_URL}/exams/${selectedExamDefinitionId}/questions`, payload, {
          headers: buildAuthHeaders(token),
        });

        const savedQuestions: AssignmentBuilderQuestionItem[] = (response.data || []).map((item: any, index: number) => ({
          id: item.id ?? 0,
          examId: selectedExamDefinitionId,
          questionId: item.questionId,
          questionText: item.questionText || '',
          categoryId: item.categoryId,
          difficultyLevel: item.difficultyLevel,
          isEnabled: item.isEnabled ?? true,
          orderIndex: item.orderIndex ?? index,
          marks: item.marks > 0 ? item.marks : 1,
          negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
        }));

        setBuilderQuestions(savedQuestions);
      } else {
        await axios.post(
          `${API_BASE_URL}/assignments/${selectedAssignmentId}/questions`,
          {
            questions: sortedQuestions.map((item, index) => ({
              questionId: item.questionId,
              orderIndex: item.orderIndex ?? index,
              marks: item.marks > 0 ? item.marks : 1,
              negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
              isRequired: true,
            })),
          },
          { headers: buildAuthHeaders(token) }
        );

        await loadManualAssignmentQuestionBuilder(selectedAssignmentId);
      }

      setBuilderQuestionMessage('Questions saved successfully.');
      setAssignmentQuestionsCache((prev) => {
        const next = { ...prev };
        delete next[selectedAssignmentId];
        return next;
      });
      await loadAssignments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save questions.');
    } finally {
      setSavingQuestionBuilder(false);
    }
  };

  const openQuestionForm = useCallback((question?: QuestionBankItem | null) => {
    const defaultCategoryId = question?.categoryId ?? categoryFilter ?? selectedCategoryId ?? courseCategoryOptions[0]?.id ?? selectedCourseId ?? null;

    setEditingQuestionFormId(question?.id ?? null);
    setShowQuestionForm(true);
    setShowAdvancedQuestionFields(false);
    setError(null);
    setBuilderQuestionMessage(null);

    if (question) {
      const questionLatexValue = (question as any)?.questionLatex ?? '';
      const answerLatexValue = (question as any)?.answerLatex ?? '';

      const graphLatexValue = (question as any)?.graphLatex ?? '';
      const promptKeyValue = (question as any)?.promptKey ?? '';
      const promptVersionValue = (question as any)?.promptVersion ?? '';
      const promptModelValue = (question as any)?.promptModel ?? '';
      const promptResponseModeValue = (question as any)?.promptResponseMode ?? '';
      const questionForOpenAIValue = (question as any)?.questionForOpenAI ?? '';
      const answerExplanationJsonValue = (question as any)?.answerExplanationJson ?? '';

      setQuestionFormData({
        categoryId: question.categoryId,
        questionText: question.questionText || '',
        questionPrefix: '',
        answerText: '',
        explanation: '',
        answerExplanation: '',
        answerExplanationJson: answerExplanationJsonValue,
        hint: '',
        difficultyLevel: question.difficultyLevel || 1,
        isActive: true,
        source: '',
        imageUrl: '',
        optionA: question.optionA || '',
        optionB: question.optionB || '',
        optionC: question.optionC || '',
        optionD: question.optionD || '',
        optionE: question.optionE || '',
        correctOption: question.correctOption || '',
        isMultipleChoice: true,
        questionLatex: questionLatexValue,
        answerLatex: answerLatexValue,
        graphLatex: graphLatexValue,
        questionForOpenAI: questionForOpenAIValue,
        promptKey: promptKeyValue,
        promptVersion: promptVersionValue,
        promptModel: promptModelValue,
        promptResponseMode: promptResponseModeValue,
      });
      return;
    }

    setQuestionFormData(buildEmptyQuestionForm(defaultCategoryId));
  }, [categoryFilter, courseCategoryOptions, selectedCategoryId, selectedCourseId]);

  const closeQuestionForm = useCallback(() => {
    setShowQuestionForm(false);
    setShowAdvancedQuestionFields(false);
    setEditingQuestionFormId(null);
    setQuestionFormData(buildEmptyQuestionForm(categoryFilter ?? selectedCategoryId ?? courseCategoryOptions[0]?.id ?? selectedCourseId ?? null));
  }, [categoryFilter, courseCategoryOptions, selectedCategoryId, selectedCourseId]);

  const handleQuestionFormChange = <K extends keyof QuestionFormData>(field: K, value: QuestionFormData[K]) => {
    setQuestionFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleQuestionImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsUploadingQuestionImage(true);
      setError(null);
      setMessage(null);

      const token = await getToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('assetType', 'question');
      uploadFormData.append('entityType', 'question');

      const response = await fetch(`${API_BASE_URL}/assets/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to upload image');
      }

      const data = await response.json();
      handleQuestionFormChange('imageUrl', data.url || '');
      setMessage('Image uploaded successfully.');
    } catch (uploadError: any) {
      console.error('Failed to upload image from assignment question form:', uploadError);
      setError(uploadError?.message || 'Failed to upload image');
    } finally {
      setIsUploadingQuestionImage(false);
      event.target.value = '';
    }
  }, [getToken]);

  const handleQuestionFormSave = useCallback(async () => {
    const categoryId = questionFormData.categoryId;

    if (!categoryId) {
      setError('Category is required.');
      return;
    }

    if (!normalizeQuestionFormText(questionFormData.questionText)) {
      setError('Question text is required.');
      return;
    }

    if (questionFormData.difficultyLevel < 1 || questionFormData.difficultyLevel > 3) {
      setError('Difficulty must be 1, 2, or 3.');
      return;
    }

    if (!isQuestionFormMultipleChoiceValid(questionFormData)) {
      setError('Multiple-choice questions require at least two options and a valid correct option (A-E).');
      return;
    }

    try {
      setSavingQuestionForm(true);
      setError(null);
      const token = await getToken();
      const payload = {
        categoryId,
        questionText: questionFormData.questionText,
        questionPrefix: questionFormData.questionPrefix,
        answerText: questionFormData.answerText,
        explanation: questionFormData.explanation,
        answerExplanation: questionFormData.answerExplanation,
        answerExplanationJson: questionFormData.answerExplanationJson || '',
        hint: questionFormData.hint,
        difficultyLevel: questionFormData.difficultyLevel,
        isActive: questionFormData.isActive,
        source: questionFormData.source,
        imageUrl: questionFormData.imageUrl || '',
        optionA: questionFormData.optionA,
        optionB: questionFormData.optionB,
        optionC: questionFormData.optionC,
        optionD: questionFormData.optionD,
        optionE: questionFormData.optionE,
        correctOption: questionFormData.correctOption,
        isMultipleChoice: questionFormData.isMultipleChoice,
        questionLatex: questionFormData.questionLatex || '',
        answerLatex: questionFormData.answerLatex || '',
        graphLatex: questionFormData.graphLatex || '',
        questionForOpenAI: questionFormData.questionForOpenAI || '',
        promptKey: questionFormData.promptKey || '',
        promptVersion: questionFormData.promptVersion || '',
        promptModel: questionFormData.promptModel || '',
        promptResponseMode: questionFormData.promptResponseMode || '',
      };

      let createdQuestionId: number | null = null;

      if (editingQuestionFormId) {
        await axios.put(`${API_BASE_URL}/questions/${editingQuestionFormId}`, payload, {
          headers: buildAuthHeaders(token),
        });
      } else {
        const createResponse = await axios.post(`${API_BASE_URL}/questions`, payload, {
          headers: buildAuthHeaders(token),
        });
        const responseId = Number(createResponse?.data?.id ?? createResponse?.data?.questionId ?? 0);
        if (Number.isFinite(responseId) && responseId > 0) {
          createdQuestionId = responseId;
        }
      }

      const shouldAttachToBuilder = !!selectedAssignmentId && !!location.pathname && location.pathname.includes('manage-assignment-builder');
      if (shouldAttachToBuilder && createdQuestionId && !editingQuestionFormId) {
        const nextBuilderQuestions = [...builderQuestions];
        const alreadySelected = nextBuilderQuestions.some((item) => item.questionId === createdQuestionId);
        if (!alreadySelected) {
          nextBuilderQuestions.push({
            id: 0,
            examId: selectedExamDefinitionId ?? 0,
            questionId: createdQuestionId,
            questionText: questionFormData.questionText,
            categoryId,
            difficultyLevel: questionFormData.difficultyLevel,
            isEnabled: true,
            orderIndex: nextBuilderQuestions.length,
            marks: 1,
            negativeMarks: 0,
          });
          setBuilderQuestions(nextBuilderQuestions);

          const savePayload = {
            questions: nextBuilderQuestions
              .sort((a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER))
              .map((item, index) => ({
                questionId: item.questionId,
                orderIndex: item.orderIndex ?? index,
                marks: item.marks > 0 ? item.marks : 1,
                negativeMarks: item.negativeMarks >= 0 ? item.negativeMarks : 0,
                isRequired: true,
              })),
          };

          if (selectedExamDefinitionId) {
            await axios.put(`${API_BASE_URL}/exams/${selectedExamDefinitionId}/questions`, savePayload, {
              headers: buildAuthHeaders(token),
            });
          } else {
            await axios.post(`${API_BASE_URL}/assignments/${selectedAssignmentId}/questions`, savePayload, {
              headers: buildAuthHeaders(token),
            });
          }
        }
      }

      setShowQuestionForm(false);
      setEditingQuestionFormId(null);
      setQuestionFormData(buildEmptyQuestionForm(categoryId));
      setBuilderQuestionMessage('Question saved successfully.');

      if (categoryId) {
        await loadQuestionBankForCategory(categoryId);
      }
      if (selectedAssignmentId && !selectedExamDefinitionId) {
        await loadManualAssignmentQuestionBuilder(selectedAssignmentId);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save question.');
    } finally {
      setSavingQuestionForm(false);
    }
  }, [buildAuthHeaders, builderQuestions, editingQuestionFormId, getToken, isBuilderPage, loadManualAssignmentQuestionBuilder, loadQuestionBankForCategory, location.pathname, questionFormData, selectedAssignmentId, selectedExamDefinitionId]);

  const selectedQuestionIds = getSelectedQuestionIds();

  const questionBankById = questionBank.reduce<Record<number, QuestionBankItem>>((map, question) => {
    map[question.id] = question;
    return map;
  }, {});

  const filteredQuestionBank = questionBank.filter((question) => {
    const search = searchText.trim().toLowerCase();
    const hasCorrectOption = !!question.correctOption && question.correctOption.trim().length > 0;
    const matchesSearch = !search || question.questionText.toLowerCase().includes(search);
    const matchesCategory = !categoryFilter || question.categoryId === categoryFilter;
    const matchesDifficulty = !difficultyFilter || question.difficultyLevel === difficultyFilter;
    const matchesCourse = !courseFilter || (courseCategoryIds[courseFilter] || []).includes(question.categoryId);
    const matchesReleaseVersion =
      !releaseVersionFilter ||
      categoryReleaseVersionMap[question.categoryId] === releaseVersionFilter;

    return hasCorrectOption && matchesSearch && matchesCategory && matchesDifficulty && matchesCourse && matchesReleaseVersion;
  });

  useEffect(() => {
    setQuestionBankPage(1);
  }, [searchText, categoryFilter, difficultyFilter, courseFilter, releaseVersionFilter, selectedExamDefinitionId]);

  const visibleQuestionBank = filteredQuestionBank.slice(0, questionBankPage * QUESTION_BANK_PAGE_SIZE);
  const hasMoreQuestionBank = visibleQuestionBank.length < filteredQuestionBank.length;

  const sortedSelectedQuestions = [...builderQuestions].sort(
    (a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
  );

  const enabledCount = builderQuestions.filter((item) => item.isEnabled).length;
  const totalMarks = builderQuestions
    .filter((item) => item.isEnabled)
    .reduce((sum, item) => sum + (item.marks > 0 ? item.marks : 0), 0);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const selectedAssignment = useMemo(
    () => assignments.find((assignment) => assignment.id === selectedAssignmentId) ?? null,
    [assignments, selectedAssignmentId]
  );

  const selectedExamDefinition = useMemo(
    () => examDefinitions.find((item) => item.id === selectedExamDefinitionId) ?? null,
    [examDefinitions, selectedExamDefinitionId]
  );

  const upsertExamDefinitionOption = useCallback((definition: Partial<ExamDefinition> | null | undefined) => {
    if (!definition || typeof definition.id !== 'number' || !definition.name || !definition.type) {
      return;
    }

    const normalizedDefinition: ExamDefinition = {
      id: definition.id as number,
      type: normalizeAssignmentType(definition.type),
      name: definition.name as string,
      timeLimitMinutes: Number(definition.timeLimitMinutes ?? 60),
      isActive: definition.isActive ?? true,
      courses: definition.courses ?? [],
    };

    setExamDefinitions((current) => {
      const next = current.filter((item) => item.id !== normalizedDefinition.id);
      next.push(normalizedDefinition);

      return next.sort((left, right) => left.name.localeCompare(right.name));
    });
  }, []);

  useEffect(() => {
    if (editorMode !== 'idle' || !selectedAssignment) {
      return;
    }

    setSelectedExamDefinitionId(selectedAssignment.examId ?? null);
    setAssignmentType(normalizeAssignmentType(selectedAssignment.type));
    setTitle(selectedAssignment.title || '');
    setExamName(selectedAssignment.examName || '');
    setDescription(selectedAssignment.description || '');
    setDueAt(selectedAssignment.dueAt ? new Date(selectedAssignment.dueAt).toISOString().slice(0, 16) : '');
    setSelectedCourseId(selectedAssignment.courseId ?? selectedCourseId ?? courseOptions[0]?.id ?? null);
    setSelectedCategoryId(selectedAssignment.categoryId ?? null);
  }, [courseOptions, editorMode, selectedAssignment, selectedCourseId]);

  useEffect(() => {
    if (!assignments.length) {
      setSelectedAssignmentId(null);
      setEditorMode('idle');
      setEditingAssignmentId(null);
      return;
    }

    if (selectedAssignmentId != null && !assignments.some((assignment) => assignment.id === selectedAssignmentId)) {
      setSelectedAssignmentId(null);
      setEditorMode('idle');
      setEditingAssignmentId(null);
    }
  }, [assignments, selectedAssignment, selectedAssignmentId]);

  const resetEditor = useCallback(() => {
    setAssignmentType('Homework');
    setSelectedExamDefinitionId(null);
    setTitle('');
    setExamName('');
    setDescription('');
    setDueAt('');
    setSelectedCourseId(courseOptions[0]?.id ?? null);
    setSelectedCategoryId(null);
    setSelectedClassroomIds([]);
    setSelectedUserIds([]);
    setBuilderQuestions([]);
    setQuestionBank([]);
    setCategoryFilter(null);
    setDifficultyFilter(null);
    setCourseFilter(null);
    setReleaseVersionFilter(null);
    setQuestionBankPage(1);
    setBuilderQuestionMessage(null);
    setEditingAssignmentId(null);
    setEditorMode('idle');
  }, [courseOptions]);

  const startCreate = useCallback(() => {
    resetEditor();
    setEditorMode('create');
    setDetailsTab('details');
    if (Number.isFinite(builderExamIdParam) && builderExamIdParam > 0) {
      setSelectedExamDefinitionId(builderExamIdParam);
    }
    if (isSmallScreen) {
      setMobilePane('details');
    }
  }, [builderExamIdParam, isSmallScreen, resetEditor]);

  const startEdit = useCallback((assignment: Assignment) => {
    setEditorMode('edit');
    setEditingAssignmentId(assignment.id);
    setDetailsTab('details');
    setSelectedAssignmentId(assignment.id);
    setSelectedExamDefinitionId(assignment.examId ?? null);
    setAssignmentType(normalizeAssignmentType(assignment.type));
    setTitle(assignment.title || '');
    setExamName(assignment.examName || '');
    setDescription(assignment.description || '');
    setDueAt(assignment.dueAt ? new Date(assignment.dueAt).toISOString().slice(0, 16) : '');
    setSelectedCourseId(assignment.courseId ?? courseOptions[0]?.id ?? null);
    setSelectedCategoryId(assignment.categoryId ?? null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  }, [courseOptions, isSmallScreen]);

  useEffect(() => {
    builderIntentHandledRef.current = false;
  }, [builderAssignmentIdParam, builderExamIdParam, builderModeParam, isBuilderPage]);

  useEffect(() => {
    if (!isBuilderPage || !isAuthenticated || builderIntentHandledRef.current) {
      return;
    }

    if (builderModeParam === 'create') {
      startCreate();
      builderIntentHandledRef.current = true;
      return;
    }

    if (builderModeParam === 'edit' && Number.isFinite(builderAssignmentIdParam) && builderAssignmentIdParam > 0) {
      const assignment = assignments.find((item) => item.id === builderAssignmentIdParam);
      if (!assignment) {
        return;
      }

      startEdit(assignment);
      if (builderTabParam === 'questions') {
        setDetailsTab('questions');
      }
      builderIntentHandledRef.current = true;
    }
  }, [assignments, builderAssignmentIdParam, builderModeParam, builderTabParam, isAuthenticated, isBuilderPage, startCreate, startEdit]);

  const openBuilderForCreate = useCallback(() => {
    navigate('/manage-assignment-builder?mode=create');
  }, [navigate]);

  const openBuilderForEdit = useCallback((assignmentId: number) => {
    navigate(`/manage-assignment-builder?mode=edit&assignmentId=${assignmentId}`);
  }, [navigate]);

  const createAssignment = useCallback(async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    if (!selectedCourseId || selectedCourseId <= 0) {
      setError('Course is required.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getToken();
      const response = await axios.post(
        `${API_BASE_URL}/assignments`,
        {
          courseId: selectedCourseId,
          categoryId: selectedCategoryId ?? null,
          examId: selectedExamDefinitionId,
          examName: selectedExamDefinitionId == null ? (examName.trim() || null) : null,
          type: assignmentType,
          title: title.trim(),
          description: description.trim() || null,
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          maxAttempts: 1,
          allowLateSubmission: false,
          shuffleQuestions: false,
          shuffleOptions: false
        },
        { headers: buildAuthHeaders(token) }
      );

      setTitle('');
      setExamName('');
      setDescription('');
      setDueAt('');
      setSelectedExamDefinitionId(null);
      setMessage('Assignment created as draft.');
      await loadAssignments();
      upsertExamDefinitionOption(response.data?.examId ? {
        id: response.data.examId,
        name: response.data.examName,
        type: response.data.type,
        timeLimitMinutes: response.data.timeLimitMinutes,
        isActive: true,
      } : null);

      const createdId = Number(response?.data?.id);
      if (Number.isFinite(createdId) && createdId > 0) {
        const createdAssignment: Assignment = {
          id: createdId,
          organizationId: Number(response.data?.organizationId ?? 0),
          courseId: response.data?.courseId ?? selectedCourseId ?? null,
          categoryId: response.data?.categoryId ?? selectedCategoryId ?? null,
          examId: response.data?.examId ?? selectedExamDefinitionId ?? null,
          examName: response.data?.examName ?? null,
          type: response.data?.type ?? assignmentType,
          title: response.data?.title ?? title.trim(),
          description: response.data?.description ?? (description.trim() || null),
          status: response.data?.status ?? 'Draft',
          dueAt: response.data?.dueAt ?? (dueAt ? new Date(dueAt).toISOString() : null),
          questionCount: Number(response.data?.questionCount ?? 0),
          targetCount: Number(response.data?.targetCount ?? 0),
        };

        startEdit(createdAssignment);
        setDetailsTab('questions');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create assignment.');
    }
  }, [assignmentType, description, dueAt, examName, getToken, loadAssignments, selectedCategoryId, selectedCourseId, selectedExamDefinitionId, startEdit, title, upsertExamDefinitionOption]);

  const updateAssignment = async () => {
    if (!editingAssignmentId) {
      setError('Select an assignment to edit.');
      return;
    }

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    if (!selectedCourseId || selectedCourseId <= 0) {
      setError('Course is required.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getToken();
      const response = await axios.put(
        `${API_BASE_URL}/assignments/${editingAssignmentId}`,
        {
          courseId: selectedCourseId,
          categoryId: selectedCategoryId ?? null,
          examId: selectedExamDefinitionId,
          examName: selectedExamDefinitionId == null ? (examName.trim() || null) : null,
          type: assignmentType,
          title: title.trim(),
          description: description.trim() || null,
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          maxAttempts: 1,
          allowLateSubmission: false,
          shuffleQuestions: false,
          shuffleOptions: false,
        },
        { headers: buildAuthHeaders(token) }
      );

      setSelectedExamDefinitionId(response.data?.examId ?? selectedExamDefinitionId ?? null);
      setExamName(response.data?.examName ?? examName);
      upsertExamDefinitionOption(response.data?.examId ? {
        id: response.data.examId,
        name: response.data.examName,
        type: response.data.type,
        timeLimitMinutes: response.data.timeLimitMinutes,
        isActive: true,
      } : null);
      setMessage('Assignment updated.');
      await loadAssignments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update assignment.');
    }
  };

  const deleteAssignment = async (assignmentId: number) => {
    try {
      setError(null);
      setMessage(null);
      const token = await getToken();
      await axios.delete(`${API_BASE_URL}/assignments/${assignmentId}`, {
        headers: buildAuthHeaders(token),
      });

      if (selectedAssignmentId === assignmentId) {
        setSelectedAssignmentId(null);
      }

      if (editingAssignmentId === assignmentId) {
        resetEditor();
      }

      setMessage('Assignment deleted.');
      await loadAssignments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete assignment.');
    }
  };

  const syncQuestionsFromExam = async () => {
    if (!selectedAssignmentId) return;
    try {
      setSyncingQuestions(true);
      setError(null);
      const token = await getToken();
      const response = await axios.post(
        `${API_BASE_URL}/assignments/${selectedAssignmentId}/sync-questions`,
        {},
        { headers: buildAuthHeaders(token) }
      );
      setMessage(response.data?.message ?? 'Questions synced successfully.');
      // Invalidate cache so questions reload
      setAssignmentQuestionsCache((prev) => {
        const next = { ...prev };
        delete next[selectedAssignmentId];
        return next;
      });
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to sync questions.');
    } finally {
      setSyncingQuestions(false);
    }
  };

  const saveAssignment = async () => {
    if (editorMode === 'edit') {
      await updateAssignment();
      return;
    }

    await createAssignment();
  };

  const attachTargets = async () => {
    if (!selectedAssignmentId) {
      setError('Select an assignment first.');
      return;
    }

    const classIds = selectedClassroomIds;
    const userIds = selectedUserIds;

    if (classIds.length === 0 && userIds.length === 0) {
      setError('Provide at least one class id or user id.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getToken();
      await axios.post(
        `${API_BASE_URL}/assignments/${selectedAssignmentId}/targets`,
        {
          targets: [
            ...classIds.map((id) => ({ targetType: 'Class', classroomId: id })),
            ...userIds.map((id) => ({ targetType: 'User', userId: id }))
          ]
        },
        { headers: buildAuthHeaders(token) }
      );

      setAssignmentTargetsCache((current) => ({
        ...current,
        [selectedAssignmentId]: {
          classroomIds: classIds,
          userIds,
        },
      }));

      setMessage('Targets updated.');
      await loadAssignments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to attach targets.');
    }
  };

  const publish = async (assignmentId?: number) => {
    const targetAssignmentId = assignmentId ?? selectedAssignmentId;
    if (!targetAssignmentId) {
      setError('Select an assignment first.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getToken();
      await axios.post(
        `${API_BASE_URL}/assignments/${targetAssignmentId}/publish`,
        {},
        { headers: buildAuthHeaders(token) }
      );
      setMessage('Assignment published.');
      await loadAssignments();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to publish assignment.');
    }
  };

  const handleSort = (columnKey: string) => {
    if (!['title', 'status', 'questions', 'targets'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<AssignmentRow>[]>(() => {
    const items: DataGridColumn<AssignmentRow>[] = [
      {
        key: 'title',
        label: 'Assignment',
        sortable: true,
        width: isSmallScreen ? '52%' : '28%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.title}</div>
            {row.assignment.examName && (
              <div style={{ fontSize: '0.72rem', color: '#44638f', marginTop: 2 }}>
                Linked: {row.assignment.examName}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        width: isSmallScreen ? '0%' : '10%',
        render: (row) => row.type,
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: isSmallScreen ? '0%' : '16%',
        render: (row) => (
          <span style={{ fontSize: '0.78rem', color: '#38557f', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {row.createdBy}
          </span>
        ),
      },
      {
        key: 'endDate',
        label: 'End Date',
        width: isSmallScreen ? '0%' : '18%',
        render: (row) => (row.endDate ? new Date(row.endDate).toLocaleString() : 'No due date'),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '22%' : '18%',
        render: (row) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              border: '1px solid #d1ddf3',
              color: '#3a5686',
              background: '#f4f8ff',
              padding: '2px 8px',
              fontSize: '0.73rem',
              fontWeight: 700,
            }}
          >
            {row.status}
          </span>
        ),
      },
    ];

    if (!isSmallScreen) {
      items.push(
        {
          key: 'questions',
          label: 'Questions',
          sortable: true,
          width: '12%',
          render: (row) => row.questions,
        },
        {
          key: 'targets',
          label: 'Targets',
          sortable: true,
          width: '12%',
          render: (row) => row.targets,
        }
      );
    }

    return items;
  }, [isSmallScreen]);

  const rowActions: DataGridRowAction<AssignmentRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <IconEdit size={14} />,
      onClick: (row) => {
        if (isBuilderPage) {
          startEdit(row.assignment);
          return;
        }

        openBuilderForEdit(row.assignment.id);
      },
      isDisabled: () => loading,
    },
    {
      key: 'publish',
      label: 'Publish',
      icon: <IconCheckCircle size={14} />,
      onClick: (row) => {
        setSelectedAssignmentId(row.assignment.id);
        void publish(row.assignment.id);
      },
      isDisabled: (row) => loading || row.assignment.status.toLowerCase() === 'published',
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <IconDelete size={14} />,
      tone: 'destructive',
      onClick: (row) => {
        setPendingDeleteId(row.assignment.id);
      },
      isDisabled: (row) => loading || row.assignment.status.toLowerCase() !== 'draft',
    },
  ];

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        value: statusFilter,
        onChange: (value) => {
          setStatusFilter(value);
          setPage(1);
        },
        options: [
          { value: 'all', label: 'All statuses' },
          { value: 'draft', label: 'Draft' },
          { value: 'published', label: 'Published' },
          { value: 'closed', label: 'Closed' },
        ],
      },
    ],
    [statusFilter]
  );

  const goBackToAssignments = useCallback(() => {
    navigate('/manage-assignments');
  }, [navigate]);

  const actions: HeaderAction[] = [
    ...(isBuilderPage
      ? [{
          key: 'back',
          label: 'Back to Assignments',
          variant: 'secondary' as const,
          onClick: goBackToAssignments,
          disabled: loading,
        }]
      : []),
    ...(!isBuilderPage
      ? [{
          key: 'create',
          label: 'Create Assignment',
          variant: 'primary' as const,
          onClick: () => {
            if (isBuilderPage) {
              startCreate();
              return;
            }

            openBuilderForCreate();
          },
          disabled: loading,
        }]
      : []),
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadAssignments();
      },
      disabled: loading,
    },
  ];

  const totalCount = assignments.length;
  const draftCount = assignments.filter((assignment) => assignment.status.toLowerCase() === 'draft').length;
  const publishedCount = assignments.filter((assignment) => assignment.status.toLowerCase() === 'published').length;
  const questionMappedCount = assignments.filter((assignment) => assignment.questionCount > 0).length;

  const showListPanel = !isBuilderPage;
  const showDetailsPanel = isBuilderPage;
  const showEmptyDetailsState = editorMode === 'idle' && !selectedAssignment;
  const isViewMode = editorMode === 'idle' && !!selectedAssignment;
  const needsLinkedDefinitionResolution = (editorMode === 'edit' || isViewMode) && !!selectedAssignment?.examId;
  const linkedDefinitionResolved = !needsLinkedDefinitionResolution
    || (!!selectedExamDefinition && selectedExamDefinition.id === selectedAssignment?.examId);
  const detailsReady = (editorMode === 'create' || referenceDataLoaded) && linkedDefinitionResolved;
  const detailsDisabled = (editorMode === 'edit' || isViewMode) && !detailsReady;
  const isBuilderWorkflow = isBuilderPage;
  const canUseQuestionStep = !!selectedAssignmentId;
  const canUseTargetStep = !!selectedAssignmentId;
  const canUseSubmissionStep = editorMode === 'edit' && !!selectedAssignmentId;

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Manage Assignments</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage assignments.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <MathJaxContext>
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Assignments', to: '/manage-assignments', isCurrent: !isBuilderPage },
          ...(isBuilderPage ? [{ label: 'Assignment Builder', isCurrent: true }] : []),
        ]}
      />

      <AdminPageHeader
        title={isBuilderPage ? 'Assignment Builder' : 'Manage Assignments'}
        subtitle={`${totalCount} assignments • ${draftCount} drafts • ${publishedCount} published`}
        actions={actions}
      />

      {(error || message) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bbf7d0'}`,
            background: error ? '#fff1f2' : '#f0fdf4',
            color: error ? '#b91c1c' : '#166534',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || message}
        </div>
      )}

      {!isBuilderPage && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            <AdminStatCard label="Total" value={totalCount} icon={<IconBook size={15} />} />
            <AdminStatCard label="Draft" value={draftCount} icon={<IconMetadata size={15} />} accent="draft" />
            <AdminStatCard label="Published" value={publishedCount} icon={<IconCheckCircle size={15} />} accent="active" />
            <AdminStatCard label="With Questions" value={questionMappedCount} icon={<IconArchive size={15} />} accent="archived" />
          </div>

          <AdminFilterBar
            searchValue={searchValue}
            onSearchChange={(value) => {
              setSearchValue(value);
              setPage(1);
            }}
            searchPlaceholder="Search assignments by title, status, type..."
            filters={filterConfigs}
          />
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, minHeight: isNarrowLayout ? 'auto' : 560 }}>
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>Loading assignments...</div>
            ) : (
              <AdminDataGrid<AssignmentRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedAssignmentId}
                onRowClick={(row) => {
                  openBuilderForEdit(row.assignment.id);
                }}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage="No assignments yet."
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button type="button" className="amd-btn amd-btn-ghost" style={{ marginBottom: 8 }} onClick={() => setMobilePane('list')}>
                  Back to List
                </button>
              )}
              <h3 style={{ margin: 0, width: '100%', boxSizing: 'border-box', fontSize: '1.02rem', fontWeight: 800, color: '#173159', background: selectedAssignment ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', border: selectedAssignment ? '1px solid #cfe0fb' : '1px solid #d9e2ef', borderRadius: 10, padding: '9px 11px' }}>
                {editorMode === 'edit' && editingAssignmentId
                  ? 'Editing Assignment'
                  : editorMode === 'create'
                    ? 'Create Assignment'
                    : selectedAssignment
                      ? selectedAssignment.title
                      : 'Select an Assignment'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              {showEmptyDetailsState ? (
                <div
                  style={{
                    minHeight: 260,
                    border: '1px dashed #c9d6ef',
                    borderRadius: 12,
                    background: '#f8fbff',
                    display: 'grid',
                    placeContent: 'center',
                    textAlign: 'center',
                    color: '#4f658f',
                    gap: 8,
                    padding: 16,
                  }}
                >
                  <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={26} /></div>
                  <div style={{ fontWeight: 700 }}>Select an assignment from the list</div>
                  <div style={{ fontSize: '0.82rem' }}>or</div>
                  <button
                    type="button"
                    className="amd-btn amd-btn-primary"
                    onClick={() => {
                      if (isBuilderPage) {
                        startCreate();
                        return;
                      }

                      openBuilderForCreate();
                    }}
                  >
                    Create a new assignment
                  </button>
                </div>
              ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <section style={{ display: 'grid', gap: 10, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  {!isBuilderWorkflow && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d8e3f6', borderRadius: 11, background: '#f6f9ff', padding: 4, width: 'fit-content' }}>
                    <button
                      type="button"
                      className={`amd-btn ${detailsTab === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                      onClick={() => setDetailsTab('details')}
                    >
                      Details
                    </button>
                    {editorMode === 'edit' && (
                      <>
                        <button
                          type="button"
                          className={`amd-btn ${detailsTab === 'questions' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                          onClick={() => setDetailsTab('questions')}
                        >
                          Attach Questions
                        </button>
                        <button
                          type="button"
                          className={`amd-btn ${detailsTab === 'targets' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                          onClick={() => setDetailsTab('targets')}
                        >
                          Assign Users/Classes
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={`amd-btn ${detailsTab === 'submissions' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                      onClick={() => setDetailsTab('submissions')}
                    >
                      Submissions
                    </button>
                  </div>
                  )}

                  {isBuilderWorkflow && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d8e3f6', borderRadius: 11, background: '#f6f9ff', padding: 4, width: 'fit-content', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`amd-btn ${detailsTab === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                        onClick={() => setDetailsTab('details')}
                      >
                        1. Details
                      </button>
                      <button
                        type="button"
                        className={`amd-btn ${detailsTab === 'questions' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                        onClick={() => setDetailsTab('questions')}
                        disabled={!canUseQuestionStep}
                        title={canUseQuestionStep ? 'Manage questions' : 'Save draft details first'}
                      >
                        2. Questions
                      </button>
                      <button
                        type="button"
                        className={`amd-btn ${detailsTab === 'targets' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                        onClick={() => setDetailsTab('targets')}
                        disabled={!canUseTargetStep}
                        title={canUseTargetStep ? 'Assign students/classes' : 'Save draft details first'}
                      >
                        3. Targets
                      </button>
                      <button
                        type="button"
                        className={`amd-btn ${detailsTab === 'submissions' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
                        onClick={() => setDetailsTab('submissions')}
                        disabled={!canUseSubmissionStep}
                        title={canUseSubmissionStep ? 'Review submissions' : 'Available in edit mode only'}
                      >
                        4. Submissions
                      </button>
                    </div>
                  )}
                </section>

                {detailsTab === 'details' && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    {detailsDisabled && (
                      <div
                        style={{
                          border: '1px solid #d9e2ef',
                          background: '#f8fbff',
                          color: '#3f5f93',
                          borderRadius: 10,
                          padding: '10px 12px',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                        }}
                      >
                        Loading linked definition...
                      </div>
                    )}
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Assignment Title
                      <input placeholder="Title" value={title} onChange={(event) => setTitle(event.target.value)} disabled={isViewMode || detailsDisabled} />
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Course
                      <select
                        value={selectedCourseId ?? ''}
                        onChange={(event) => {
                          const nextCourseId = event.target.value ? Number(event.target.value) : null;
                          setSelectedCourseId(nextCourseId);
                          setSelectedCategoryId(null);
                        }}
                        disabled={isViewMode || detailsDisabled || loadingCourseOptions}
                      >
                        <option value="">Select a course</option>
                        {courseOptions.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.pathLabel || course.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Category (optional)
                      <select
                        value={selectedCategoryId ?? ''}
                        onChange={(event) => {
                          const nextCategoryId = event.target.value ? Number(event.target.value) : null;
                          setSelectedCategoryId(nextCategoryId);
                        }}
                        disabled={isViewMode || detailsDisabled || !selectedCourseId || loadingCourseCategories}
                      >
                        <option value="">No category selected</option>
                        {courseCategoryOptions.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.pathLabel || category.label || category.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Type
                      <select
                        value={assignmentType}
                        disabled={isViewMode || detailsDisabled}
                        onChange={(event) => {
                          const nextType = event.target.value;

                          const shouldWarnOnTypeChange =
                            editorMode === 'edit'
                            && !!selectedAssignment
                            && selectedAssignment.status.toLowerCase() === 'draft'
                            && selectedAssignment.questionCount > 0
                            && selectedExamDefinitionId != null
                            && assignmentType !== nextType;

                          if (shouldWarnOnTypeChange) {
                            const confirmed = window.confirm(
                              'Changing type will unlink the current definition in this form and can change assignment questions after publish. Continue?'
                            );

                            if (!confirmed) {
                              return;
                            }
                          }

                          setAssignmentType(normalizeAssignmentType(nextType));
                          if (selectedExamDefinitionId != null) {
                            setSelectedExamDefinitionId(null);
                            setExamName('');
                          }
                        }}
                      >
                        <option value="Homework">Homework</option>
                        <option value="Exam">Exam</option>
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Linked Definition
                    <select
                      value={selectedExamDefinitionId ?? ''}
                      disabled={isViewMode || detailsDisabled}
                      onChange={(event) => {
                        const nextId = event.target.value ? Number(event.target.value) : null;

                        const shouldWarnOnChange =
                          editorMode === 'edit'
                          && !!selectedAssignment
                          && selectedAssignment.status.toLowerCase() === 'draft'
                          && selectedAssignment.questionCount > 0
                          && selectedAssignment.examId !== nextId;

                        if (shouldWarnOnChange) {
                          const confirmed = window.confirm(
                            'Changing the linked definition can clear or replace existing assignment questions when published. Continue?'
                          );

                          if (!confirmed) {
                            return;
                          }
                        }

                        setSelectedExamDefinitionId(nextId);
                        if (nextId != null) {
                          const match = examDefinitions.find((item) => item.id === nextId);
                          if (match?.type) {
                            setAssignmentType(normalizeAssignmentType(match.type));
                          }
                        } else {
                          setExamName('');
                        }
                      }}
                    >
                      <option value="">No linked definition (manual questions)</option>
                      {examDefinitions
                        .filter((item) =>
                          (item.isActive || item.id === selectedExamDefinitionId)
                          && normalizeAssignmentType(item.type) === normalizeAssignmentType(assignmentType)
                        )
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.type} - {item.name}
                          </option>
                        ))}
                    </select>
                    </label>
                    {selectedExamDefinition && (
                      <div style={{ fontSize: '0.82rem', color: '#49638d' }}>
                        Linked to {selectedExamDefinition.type} definition "{selectedExamDefinition.name}".
                      </div>
                    )}
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      {assignmentType === 'Exam' ? 'Exam Name' : 'Homework Name'}
                      <input
                        placeholder={assignmentType === 'Exam' ? 'Enter exam name' : 'Enter homework name'}
                        value={selectedExamDefinition ? selectedExamDefinition.name : examName}
                        onChange={(event) => setExamName(event.target.value)}
                        disabled={isViewMode || detailsDisabled || selectedExamDefinitionId != null}
                      />
                    </label>
                    <div style={{ fontSize: '0.78rem', color: '#607399' }}>
                      {selectedExamDefinitionId == null
                        ? 'When no linked definition is selected, this name creates or reuses a dedicated definition record for this assignment flow.'
                        : 'Linked definitions keep their existing name. Clear the linked definition only if you want this assignment to create its own dedicated record.'}
                    </div>
                    <textarea placeholder="Description" value={description} onChange={(event) => setDescription(event.target.value)} rows={3} disabled={isViewMode || detailsDisabled} />
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.8rem', color: '#49638d' }}>
                      Due Date and Time
                      <input type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} disabled={isViewMode || detailsDisabled} />
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {isViewMode ? (
                        <button
                          type="button"
                          className="amd-btn amd-btn-primary"
                          onClick={() => {
                            if (!selectedAssignment) {
                              return;
                            }

                            if (isBuilderPage) {
                              startEdit(selectedAssignment);
                              return;
                            }

                            openBuilderForEdit(selectedAssignment.id);
                          }}
                          disabled={loading || !selectedAssignment || detailsDisabled}
                        >
                          Edit Assignment
                        </button>
                      ) : (
                        <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void saveAssignment(); }} disabled={loading || detailsDisabled}>
                          {editorMode === 'edit' ? 'Save Changes' : 'Save Draft'}
                        </button>
                      )}
                      {editorMode === 'edit' && editingAssignmentId && (
                        <>
                          <button
                            type="button"
                            className="amd-btn amd-btn-ghost"
                            onClick={() => {
                              if (selectedAssignment) {
                                startEdit(selectedAssignment);
                              } else {
                                resetEditor();
                              }
                            }}
                            disabled={loading || detailsDisabled}
                          >
                            Cancel Edit
                          </button>
                          <button
                            type="button"
                            className="amd-btn amd-btn-ghost"
                            onClick={() => setPendingDeleteId(editingAssignmentId)}
                            disabled={loading || detailsDisabled}
                          >
                            Delete Draft
                          </button>
                        </>
                      )}

                      {isBuilderWorkflow && !!selectedAssignmentId && (
                        <button
                          type="button"
                          className="amd-btn amd-btn-secondary"
                          onClick={() => setDetailsTab('questions')}
                          disabled={loading || detailsDisabled}
                        >
                          Continue to Questions
                        </button>
                      )}
                    </div>

                    {!isBuilderWorkflow && (
                      <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void publish(); }} disabled={loading || !selectedAssignmentId || detailsDisabled}>
                        Publish Assignment
                      </button>
                    )}
                  </section>
                )}

                {detailsTab === 'questions' && (
                  <section style={{ display: 'grid', gap: 12, minHeight: 0 }}>
                    {isBuilderWorkflow ? (
                      <>
                        <AdminSectionHeading icon={<IconBook size={14} />} title="Question Assignment" />

                        {!selectedAssignmentId ? (
                          <div style={{ fontSize: '0.84rem', color: '#475f89' }}>
                            Save assignment details first to start adding questions.
                          </div>
                        ) : (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ fontSize: '0.84rem', color: '#49638d' }}>
                                {selectedExamDefinitionId
                                  ? 'Mapped questions are listed below. Add or manage them from the full question builder.'
                                  : 'Mapped questions are listed below. Link this assignment to a definition to add questions in the full builder.'}
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="amd-btn amd-btn-primary"
                                  onClick={() => openQuestionForm()}
                                  disabled={loading}
                                >
                                  Add Questions
                                </button>
                                {selectedAssignmentId && (
                                  <button
                                    type="button"
                                    className="amd-btn amd-btn-secondary"
                                    onClick={() => {
                                      const returnTo = `/manage-assignment-builder?mode=edit&assignmentId=${selectedAssignmentId}&tab=questions`;

                                      if (selectedExamDefinitionId) {
                                        navigate(`/manage-exams/${selectedExamDefinitionId}/questions?returnTo=${encodeURIComponent(returnTo)}`);
                                        return;
                                      }

                                      navigate(`/manage-exams?returnTo=${encodeURIComponent(returnTo)}`);
                                    }}
                                    disabled={loading}
                                  >
                                    Open Full Builder
                                  </button>
                                )}
                                {selectedAssignment?.examId && (
                                  <button
                                    type="button"
                                    className="amd-btn amd-btn-secondary"
                                    onClick={() => { void syncQuestionsFromExam(); }}
                                    disabled={loading || syncingQuestions}
                                  >
                                    {syncingQuestions ? 'Syncing...' : 'Sync to Assignment'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {builderQuestionMessage && (
                              <div style={{ borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', padding: '8px 10px', fontSize: '0.82rem' }}>
                                {builderQuestionMessage}
                              </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                              <div style={{ fontSize: 12, color: '#586f96' }}>
                                <strong>{sortedSelectedQuestions.length}</strong> mapped question{sortedSelectedQuestions.length === 1 ? '' : 's'} • <strong>{enabledCount}</strong> enabled • <strong>{totalMarks.toFixed(2)}</strong> marks
                              </div>
                              <button
                                type="button"
                                className="amd-btn amd-btn-primary"
                                onClick={() => { void saveBuilderQuestions(); }}
                                disabled={savingQuestionBuilder || loadingQuestionBuilder || detailsDisabled || !selectedAssignmentId}
                              >
                                {savingQuestionBuilder ? 'Saving...' : 'Save Changes'}
                              </button>
                            </div>

                            {loadingQuestionBuilder ? (
                              <div style={{ padding: 16, color: '#586f96' }}>Loading mapped questions...</div>
                            ) : (
                              <div style={{ display: 'grid', gap: 10, maxHeight: 540, overflowY: 'auto', paddingRight: 4 }}>
                                {sortedSelectedQuestions.length === 0 ? (
                                  <div style={{ border: '1px solid #edf2fb', borderRadius: 10, padding: 16, background: '#fbfdff', color: '#586f96' }}>
                                    No questions are mapped to this assignment yet.
                                  </div>
                                ) : (
                                  sortedSelectedQuestions.map((item, index) => (
                                    <div key={item.questionId} style={{ border: '1px solid #edf2fb', borderRadius: 10, padding: 12, background: '#fbfdff' }}>
                                      <div style={{ fontSize: 12, color: '#586f96', marginBottom: 8 }}>
                                        Q{index + 1} • {categoryNameMap[item.categoryId] || `Category ${item.categoryId}`} • Difficulty {item.difficultyLevel || 'N/A'}
                                      </div>
                                      <div style={{ color: '#1f3358', lineHeight: 1.45 }}>
                                        <MathJax dynamic>
                                          <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(item.questionText) }} />
                                        </MathJax>
                                      </div>

                                      {(item.imageUrl || (item as any)?.imageUrl) && (
                                        <div style={{ marginTop: 10, display: 'flex' }}>
                                          <img
                                            src={item.imageUrl || (item as any).imageUrl}
                                            alt="Question preview"
                                            style={{
                                              width: 180,
                                              height: 110,
                                              objectFit: 'contain',
                                              borderRadius: 8,
                                              border: '1px solid #dfeafc',
                                              background: '#fff',
                                              display: 'block',
                                            }}
                                          />
                                        </div>
                                      )}

                                      <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : 'auto 1fr 1fr 1fr auto auto', gap: 8, alignItems: 'center', marginTop: 10 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#1f3358' }}>
                                          <input
                                            type="checkbox"
                                            checked={item.isEnabled}
                                            onChange={(event) => updateBuilderQuestion(item.questionId, { isEnabled: event.target.checked })}
                                            disabled={detailsDisabled}
                                            title="Enable or disable this question"
                                          />
                                          Enabled
                                        </label>
                                        <input
                                          type="number"
                                          min={0}
                                          value={item.orderIndex ?? 0}
                                          onChange={(event) => updateBuilderQuestion(item.questionId, { orderIndex: Number(event.target.value) })}
                                          title="Order"
                                          disabled={detailsDisabled}
                                        />
                                        <input
                                          type="number"
                                          min={0.25}
                                          step={0.25}
                                          value={item.marks}
                                          onChange={(event) => updateBuilderQuestion(item.questionId, { marks: Number(event.target.value) })}
                                          title="Marks"
                                          disabled={detailsDisabled}
                                        />
                                        <input
                                          type="number"
                                          min={0}
                                          step={0.25}
                                          value={item.negativeMarks}
                                          onChange={(event) => updateBuilderQuestion(item.questionId, { negativeMarks: Number(event.target.value) })}
                                          title="Negative Marks"
                                          disabled={detailsDisabled}
                                        />
                                        <button
                                          type="button"
                                          className="amd-btn amd-btn-ghost"
                                          onClick={() => handleEditMappedQuestion(item)}
                                          disabled={detailsDisabled}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="amd-btn amd-btn-ghost"
                                          onClick={() => removeQuestionFromBuilder(item.questionId)}
                                          disabled={detailsDisabled}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="amd-btn amd-btn-secondary"
                                onClick={() => setDetailsTab('targets')}
                                disabled={!selectedAssignmentId}
                              >
                                Continue to Targets
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: '0.82rem', color: '#586f96', border: '1px solid #edf2fb', borderRadius: 10, padding: 10, background: '#fbfdff' }}>
                          {assignmentQuestionsLoading ? (
                            <div>Loading mapped questions...</div>
                          ) : assignmentQuestions.length === 0 ? (
                            <div>No mapped questions yet.</div>
                          ) : (
                            assignmentQuestions.map((question, index) => (
                              <div key={question.id} style={{ padding: '6px 0', borderBottom: index === assignmentQuestions.length - 1 ? 'none' : '1px solid #eef3fc' }}>
                                <div>
                                  <span style={{ fontWeight: 600, color: '#324f7c' }}>Q{index + 1}</span>
                                  <span style={{ color: '#324f7c' }}> (ID: {question.questionId}): </span>
                                  <span>{question.questionText?.replace(/<[^>]+>/g, '').trim() || 'Untitled question'}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #edf2fb', paddingTop: 10, marginTop: 2 }}>
                          {selectedAssignment?.examId ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              <button
                                type="button"
                                className="amd-btn amd-btn-secondary"
                                onClick={() => navigate(`/manage-exams/${selectedAssignment.examId}/questions`)}
                                disabled={loading || syncingQuestions}
                              >
                                Manage Questions
                              </button>
                              <button
                                type="button"
                                className="amd-btn amd-btn-primary"
                                onClick={() => { void syncQuestionsFromExam(); }}
                                disabled={loading || syncingQuestions}
                                title="Re-sync questions from the linked exam/homework definition"
                              >
                                {syncingQuestions ? 'Syncing...' : 'Sync from Exam'}
                              </button>
                            </div>
                          ) : (
                            <div style={{ fontSize: '0.82rem', color: '#49638d' }}>
                              This assignment is not linked to a definition.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </section>
                )}

                {detailsTab === 'targets' && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    <AdminSectionHeading icon={<IconBook size={14} />} title="Assign Users and Classes" />
                    {assignmentTargetsLoading && (
                      <div style={{ fontSize: '0.82rem', color: '#49638d' }}>Loading existing targets...</div>
                    )}
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: '0.82rem', color: '#324f7c', fontWeight: 700 }}>Select Classes</div>
                      <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #edf2fb', borderRadius: 10, padding: 8, background: '#fbfdff', display: 'grid', gap: 6 }}>
                        {classrooms.length === 0 ? (
                          <div style={{ fontSize: '0.82rem', color: '#586f96' }}>No classes found.</div>
                        ) : (
                          classrooms.map((classroom) => (
                            <label key={classroom.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#1f3358' }}>
                              <input
                                type="checkbox"
                                checked={selectedClassroomIds.includes(classroom.id)}
                                onChange={(event) => {
                                  setSelectedClassroomIds((current) =>
                                    event.target.checked
                                      ? Array.from(new Set([...current, classroom.id]))
                                      : current.filter((id) => id !== classroom.id)
                                  );
                                }}
                                disabled={loading || detailsDisabled}
                              />
                              {classroom.name} (ID: {classroom.id})
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: '0.82rem', color: '#324f7c', fontWeight: 700 }}>Select Users</div>
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #edf2fb', borderRadius: 10, padding: 8, background: '#fbfdff', display: 'grid', gap: 6 }}>
                        {users.length === 0 ? (
                          <div style={{ fontSize: '0.82rem', color: '#586f96' }}>No users found.</div>
                        ) : (
                          users.map((user) => (
                            <label key={user.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#1f3358' }}>
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(user.userId)}
                                onChange={(event) => {
                                  setSelectedUserIds((current) =>
                                    event.target.checked
                                      ? Array.from(new Set([...current, user.userId]))
                                      : current.filter((id) => id !== user.userId)
                                  );
                                }}
                                disabled={loading || detailsDisabled}
                              />
                              {user.displayName} {user.email ? `(${user.email})` : ''}
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    <button type="button" className="amd-btn amd-btn-secondary" onClick={() => { void attachTargets(); }} disabled={loading || detailsDisabled || !selectedAssignmentId}>
                      Save Targets
                    </button>
                    {isBuilderWorkflow && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          type="button"
                          className="amd-btn amd-btn-ghost"
                          onClick={() => setDetailsTab('questions')}
                          disabled={!selectedAssignmentId}
                        >
                          Back to Questions
                        </button>
                        <button
                          type="button"
                          className="amd-btn amd-btn-primary"
                          onClick={() => { void publish(); }}
                          disabled={loading || !selectedAssignmentId || detailsDisabled}
                        >
                          Publish Assignment
                        </button>
                      </div>
                    )}
                    <div style={{ fontSize: '0.82rem', color: '#586f96' }}>
                      Selected classes: {selectedClassroomIds.length} • Selected users: {selectedUserIds.length}
                    </div>
                  </section>
                )}

                {detailsTab === 'submissions' && selectedAssignmentId && (!isBuilderWorkflow || editorMode === 'edit') && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    <AdminSectionHeading icon={<IconCheckCircle size={14} />} title="Student Submissions" />
                    {submissionsLoading && (
                      <div style={{ fontSize: '0.82rem', color: '#49638d' }}>Loading submissions...</div>
                    )}
                    {!submissionsLoading && (!submissionsCache[selectedAssignmentId] || submissionsCache[selectedAssignmentId].length === 0) && (
                      <div style={{ fontSize: '0.82rem', color: '#586f96', padding: 16, background: '#f8fbff', borderRadius: 10, border: '1px solid #edf2fb' }}>
                        No submissions yet.
                      </div>
                    )}
                    {submissionsCache[selectedAssignmentId] && submissionsCache[selectedAssignmentId].length > 0 && (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                          <thead>
                            <tr style={{ background: '#f0f5fc', borderBottom: '2px solid #d8e3f6' }}>
                              <th style={{ padding: 12, textAlign: 'left', fontWeight: 700, color: '#324f7c' }}>Student</th>
                              <th style={{ padding: 12, textAlign: 'left', fontWeight: 700, color: '#324f7c' }}>Email</th>
                              <th style={{ padding: 12, textAlign: 'left', fontWeight: 700, color: '#324f7c' }}>Status</th>
                              <th style={{ padding: 12, textAlign: 'left', fontWeight: 700, color: '#324f7c' }}>Attempt</th>
                              <th style={{ padding: 12, textAlign: 'left', fontWeight: 700, color: '#324f7c' }}>Last Updated</th>
                              <th style={{ padding: 12, textAlign: 'center', fontWeight: 700, color: '#324f7c' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissionsCache[selectedAssignmentId].map((submission) => (
                              <tr key={submission.id} style={{ borderBottom: '1px solid #edf2fb' }}>
                                <td style={{ padding: 12, color: '#1f3358' }}>{submission.studentName}</td>
                                <td style={{ padding: 12, color: '#586f96' }}>{submission.studentEmail}</td>
                                <td style={{ padding: 12 }}>
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    background: submission.status === 'Submitted' ? '#d1f5d9' : '#fff3cd',
                                    color: submission.status === 'Submitted' ? '#155724' : '#856404'
                                  }}>
                                    {submission.status}
                                  </span>
                                </td>
                                <td style={{ padding: 12, color: '#1f3358' }}>#{submission.attemptNumber}</td>
                                <td style={{ padding: 12, color: '#586f96', fontSize: '0.82rem' }}>
                                  {new Date(submission.updatedAt || submission.submittedAt || submission.startedAt).toLocaleString()}
                                </td>
                                <td style={{ padding: 12, textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    className="amd-btn amd-btn-secondary"
                                    onClick={() => {
                                      void openSubmissionPreview(submission.id);
                                    }}
                                    style={{ fontSize: '0.82rem', padding: '4px 12px' }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                )}

                {editorMode === 'create' && detailsTab !== 'details' && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    <div style={{ fontSize: '0.84rem', color: '#475f89' }}>
                      Attach Questions and Assign Users/Classes are available after the draft is created and opened in edit mode.
                    </div>
                  </section>
                )}
              </div>
              )}
            </div>

            {isBuilderWorkflow && (
              <div style={{ padding: '0 14px 14px', borderTop: '1px solid #edf2fb', marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="amd-btn amd-btn-secondary"
                    onClick={goBackToAssignments}
                    disabled={loading}
                  >
                    Back to Assignments
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDeleteId != null}
        title="Delete assignment"
        message="Delete this draft assignment? This cannot be undone."
        onClose={() => setPendingDeleteId(null)}
        actions={[
          {
            label: 'Cancel',
            variant: 'default',
            onClick: () => setPendingDeleteId(null),
            disabled: loading,
          },
          {
            label: 'Delete',
            variant: 'destructive',
            disabled: loading || pendingDeleteId == null,
            onClick: () => {
              if (pendingDeleteId == null) {
                return;
              }

              void deleteAssignment(pendingDeleteId);
              setPendingDeleteId(null);
            },
          },
        ]}
      />

      {showQuestionForm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '28px 28px 36px',
            zIndex: 1200,
          }}
          onClick={closeQuestionForm}
        >
          <div
            style={{
              width: 'min(1120px, 94vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
              backgroundColor: '#fff',
              borderRadius: 14,
              boxShadow: '0 22px 70px rgba(15, 23, 42, 0.18)',
              padding: 20,
              border: '1px solid rgba(148, 163, 184, 0.35)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                marginBottom: 16,
                padding: '14px 16px',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)',
                boxShadow: '0 6px 18px rgba(37, 99, 235, 0.06)',
              }}
            >
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: 18, fontWeight: 700 }}>{editingQuestionFormId ? `Edit Question #${editingQuestionFormId}` : 'Create Question'}</h3>
              <button type="button" onClick={closeQuestionForm} style={{ background: 'transparent', border: '1px solid #cdd5df', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', color: '#334155' }}>Close</button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Course</label>
                  <select
                    value={selectedCourseId ?? ''}
                    onChange={(event) => {
                      const nextCourseId = event.target.value ? Number(event.target.value) : null;
                      setSelectedCourseId(nextCourseId);
                      setQuestionFormData((previous) => ({
                        ...previous,
                        categoryId: nextCourseId === selectedCourseId ? previous.categoryId : (courseCategoryOptions[0]?.id ?? previous.categoryId ?? null),
                      }));
                    }}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select course</option>
                    {courseOptions.map((course) => (
                      <option key={course.id} value={course.id}>{course.pathLabel || course.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Category *</label>
                  <select
                    value={questionFormData.categoryId ?? ''}
                    onChange={(event) => handleQuestionFormChange('categoryId', event.target.value ? Number(event.target.value) : null)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select category</option>
                    {courseCategoryOptions.map((category) => (
                      <option key={category.id} value={category.id}>{category.pathLabel || category.label || category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Difficulty *</label>
                  <select
                    value={questionFormData.difficultyLevel}
                    onChange={(event) => handleQuestionFormChange('difficultyLevel', Number(event.target.value))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value={1}>Easy</option>
                    <option value={2}>Medium</option>
                    <option value={3}>Hard</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Type</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', minHeight: 42 }}>
                    <input
                      type="checkbox"
                      checked={questionFormData.isMultipleChoice}
                      onChange={(event) => handleQuestionFormChange('isMultipleChoice', event.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>Multiple choice</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Status</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', minHeight: 42 }}>
                    <input
                      type="checkbox"
                      checked={questionFormData.isActive}
                      onChange={(event) => handleQuestionFormChange('isActive', event.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>Active</span>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Text *</label>
                <textarea
                  value={questionFormData.questionText}
                  onChange={(event) => handleQuestionFormChange('questionText', event.target.value)}
                  rows={6}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Prefix</label>
                  <input
                    type="text"
                    value={questionFormData.questionPrefix}
                    onChange={(event) => handleQuestionFormChange('questionPrefix', event.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Source</label>
                  <input
                    type="text"
                    value={questionFormData.source}
                    onChange={(event) => handleQuestionFormChange('source', event.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ display: 'block', marginBottom: 0, fontWeight: 600 }}>Question Image</label>
                  {questionFormData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => handleQuestionFormChange('imageUrl', '')}
                      style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#b91c1c' }}
                    >
                      Remove image
                    </button>
                  )}
                </div>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQuestionImageUpload}
                  disabled={isUploadingQuestionImage}
                  style={{ width: '100%', marginTop: 8 }}
                />

                {isUploadingQuestionImage && (
                  <div style={{ marginTop: 8, color: '#0f172a', fontSize: 13, fontWeight: 600 }}>Uploading image...</div>
                )}

                {questionFormData.imageUrl && (
                  <div style={{ marginTop: 12 }}>
                    <img
                      src={questionFormData.imageUrl}
                      alt="Question preview"
                      style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #dfe7f3', display: 'block', backgroundColor: '#fff' }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Text</label>
                  <input
                    type="text"
                    value={questionFormData.answerText}
                    onChange={(event) => handleQuestionFormChange('answerText', event.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Hint</label>
                  <input
                    type="text"
                    value={questionFormData.hint}
                    onChange={(event) => handleQuestionFormChange('hint', event.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
                <button
                  type="button"
                  onClick={() => setShowAdvancedQuestionFields((previous) => !previous)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: 700,
                    color: '#0f172a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Advanced (optional)</span>
                  <span>{showAdvancedQuestionFields ? 'Hide' : 'Show'}</span>
                </button>
                <div style={{ marginTop: 10, color: '#475569', fontSize: 12 }}>
                  Image uploads use the standard blob-backed question asset flow. AI and LaTeX metadata remain optional advanced fields.
                </div>

                {showAdvancedQuestionFields && (
                  <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question LaTeX</label>
                      <textarea
                        value={questionFormData.questionLatex}
                        onChange={(event) => handleQuestionFormChange('questionLatex', event.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer LaTeX</label>
                      <textarea
                        value={questionFormData.answerLatex}
                        onChange={(event) => handleQuestionFormChange('answerLatex', event.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Graph LaTeX</label>
                      <input
                        type="text"
                        value={questionFormData.graphLatex}
                        onChange={(event) => handleQuestionFormChange('graphLatex', event.target.value)}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question for OpenAI</label>
                      <textarea
                        value={questionFormData.questionForOpenAI}
                        onChange={(event) => handleQuestionFormChange('questionForOpenAI', event.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Explanation JSON</label>
                      <textarea
                        value={questionFormData.answerExplanationJson}
                        onChange={(event) => handleQuestionFormChange('answerExplanationJson', event.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Key</label>
                        <input
                          type="text"
                          value={questionFormData.promptKey}
                          onChange={(event) => handleQuestionFormChange('promptKey', event.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Version</label>
                        <input
                          type="text"
                          value={questionFormData.promptVersion}
                          onChange={(event) => handleQuestionFormChange('promptVersion', event.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Model</label>
                        <input
                          type="text"
                          value={questionFormData.promptModel}
                          onChange={(event) => handleQuestionFormChange('promptModel', event.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Response Mode</label>
                        <input
                          type="text"
                          value={questionFormData.promptResponseMode}
                          onChange={(event) => handleQuestionFormChange('promptResponseMode', event.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Explanation</label>
                <textarea
                  value={questionFormData.explanation}
                  onChange={(event) => handleQuestionFormChange('explanation', event.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Explanation</label>
                <textarea
                  value={questionFormData.answerExplanation}
                  onChange={(event) => handleQuestionFormChange('answerExplanation', event.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              {questionFormData.isMultipleChoice && (
                <div style={{ border: '1px solid #eceff4', borderRadius: 8, padding: 12, backgroundColor: '#fafafa' }}>
                  <h4 style={{ marginTop: 0, marginBottom: 10 }}>Multiple Choice Options</h4>
                  <div style={{ display: 'grid', gap: 10 }}>
                    {(['optionA', 'optionB', 'optionC', 'optionD', 'optionE'] as const).map((field, index) => (
                      <div key={field}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>{String.fromCharCode(65 + index)}</label>
                        <input
                          type="text"
                          value={questionFormData[field]}
                          onChange={(event) => handleQuestionFormChange(field, event.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Correct Option</label>
                    <select
                      value={questionFormData.correctOption}
                      onChange={(event) => handleQuestionFormChange('correctOption', event.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                    >
                      <option value="">Select correct option</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleQuestionFormSave}
                  disabled={savingQuestionForm}
                  style={{ backgroundColor: savingQuestionForm ? '#90caf9' : '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: savingQuestionForm ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                >
                  {savingQuestionForm ? 'Saving...' : editingQuestionFormId ? 'Update Question' : 'Create Question'}
                </button>
                <button
                  type="button"
                  onClick={closeQuestionForm}
                  style={{ backgroundColor: '#757575', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSubmissionId != null && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.42)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 1500,
            padding: isSmallScreen ? 10 : 20,
          }}
          onClick={closeSubmissionPreview}
        >
          <div
            style={{
              width: 'min(1100px, 100%)',
              maxHeight: '92vh',
              overflow: 'auto',
              borderRadius: 12,
              border: '1px solid #d8e3f6',
              background: '#fff',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.24)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <SubmissionReview
              attemptId={selectedSubmissionId}
              embedded
              onClose={closeSubmissionPreview}
              onOpenFullPage={(attemptId) => {
                navigate(`/admin/submissions/${attemptId}`, { state: { fromAdmin: true } });
              }}
            />
          </div>
        </div>
      )}
    </div>
    </MathJaxContext>
  );
};

export default ManageAssignments;
